/**
 * V3.4: Drop zone component for pivot builder
 */

import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { DraggableField, type FieldItem } from './DraggableField'

interface DropZoneProps {
	id: string
	title: string
	items: FieldItem[]
	onRemoveItem?: (itemId: string) => void
	emptyText?: string
	className?: string
	testId?: string
}

export function DropZone({
	id,
	title,
	items,
	onRemoveItem,
	emptyText = 'Drag fields here',
	className = '',
	testId,
}: DropZoneProps) {
	const { isOver, setNodeRef } = useDroppable({ id })

	return (
		<div
			data-testid={testId}
			className={`
        flex flex-col gap-2 p-3 rounded-lg border-2 border-dashed min-h-[80px]
        transition-all
        ${isOver ? 'border-blue-400 bg-blue-50' : 'border-neutral-300 bg-neutral-50'}
        ${className}
      `}>
			<div className='flex items-center justify-between'>
				<span className='text-xs font-semibold text-neutral-500 uppercase tracking-wide'>{title}</span>
				{items.length > 0 && (
					<span className='text-[10px] bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded-full'>{items.length}</span>
				)}
			</div>
			<div ref={setNodeRef} className='flex flex-wrap gap-1.5 min-h-[32px]'>
				<SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
					{items.length === 0 ? (
						<span className='text-xs text-neutral-400 italic'>{emptyText}</span>
					) : (
						items.map((item) => (
							<DraggableField
								key={item.id}
								item={item}
								onRemove={onRemoveItem ? () => onRemoveItem(item.id) : undefined}
							/>
						))
					)}
				</SortableContext>
			</div>
		</div>
	)
}
